<!-- Copyright 2021 Adobe. All rights reserved.
This file is licensed to you under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License. You may obtain a copy
of the License at http://www.apache.org/licenses/LICENSE-2.0
Unless required by applicable law or agreed to in writing, software distributed under
the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
OF ANY KIND, either express or implied. See the License for the specific language
governing permissions and limitations under the License. -->

import {Layout} from '@react-spectrum/docs';
export default Layout;

import docs from 'docs:@react-aria/utils';
import {HeaderInfo, FunctionAPI} from '@react-spectrum/docs';
import packageData from '@react-aria/utils/package.json';

```tsx import
import {useTextField} from '@react-aria/textfield';
import {useObjectRef} from '@react-aria/utils';
```

---
category: Utilities
keywords: [refs]
---

# useObjectRef

<p>
Offers an object ref for a given callback ref or an object ref. Especially
helfpul when passing forwarded refs (created using <code>React.forwardRef</code>)
to React Aria Hooks.
</p>

<HeaderInfo
  packageData={packageData}
  componentNames={['useObjectRef']} />

## API

<FunctionAPI function={docs.exports.useObjectRef} links={docs.links} />

## Introduction

The `useObjectRef` hook creates an object ref for a given [ref](https://reactjs.org/docs/refs-and-the-dom.html).
The given ref may be an object ref or a [callback ref](https://reactjs.org/docs/refs-and-the-dom.html#callback-refs).
The created ref is particularly helpful when defining a custom component using
[`React.forwardRef`](https://reactjs.org/docs/forwarding-refs.html), and wanting to pass the forwarded ref to
a `@react-aria` Hook that accepts a ref (e.g. [`useButton`](useButton.html#example)).

## Example using an object ref

```tsx example
const TextField = React.forwardRef((props, forwardedRef) => {
  const {label} = props;
  const ref = useObjectRef(forwardedRef);

  const {labelProps, inputProps} = useTextField(props, ref);

  return (
    <div style={{display: 'flex', flexDirection: 'column', width: 200}}>
      <label {...labelProps}>{label}</label>
      <input {...inputProps} ref={ref} />
    </div>
  );
});

function Example() {
  const textFieldRef = React.useRef();

  return (
    <div>
      <p>
        <TextField
          label="Email"
          placeholder="abc@example.com"
          ref={textFieldRef} />
      </p>
      <p>
        <button onClick={() => textFieldRef.current.focus()}>
          Click me to focus text field
        </button>
      </p>
    </div>
  );
}

<Example />
```

## Example using a callback ref

```tsx example
const TextField = React.forwardRef((props, forwardedRef) => {
  // Same as in the previous example...
///- begin collapse -///
  const {label} = props;
  const ref = useObjectRef(forwardedRef);

  const {labelProps, inputProps} = useTextField(props, ref);

  return (
    <div style={{display: 'flex', flexDirection: 'column', width: 200}}>
      <label {...labelProps}>{label}</label>
      <input {...inputProps} ref={ref} />
    </div>
  );
///- end collapse -///
});

function Example() {
  const textFieldRef = React.useRef();

  return (
    <div>
      <p>
        <TextField
          label="Email"
          placeholder="abc@example.com"
          ref={el => textFieldRef.current = el} />
      </p>
      <p>
        <button onClick={() => textFieldRef.current.focus()}>
          Click me to focus text field
        </button>
      </p>
    </div>
  );
}

<Example />
```
